import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, KeyRound, Loader2, Mail, ShieldCheck, UserRoundPlus } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";
import { ATHLETE_TOKEN_KEY as TOKEN_KEY, notifyAthleteAuthChanged } from "@/components/AccountMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#FBF9F5]/60 border border-[#0D1117]/15 rounded-xl px-4 py-3 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300";

const EMPTY = {
  name: "", email: "", phone: "", dob: "", gender: "", city: "",
  tshirt_size: "", blood_group: "", emergency_name: "", emergency_phone: "", emergency_relationship: "",
};

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] mb-2">{label}</label>
      {children}
    </div>
  );
}

export default function Signup() {
  const [form, setForm] = useState(EMPTY);
  const [code, setCode] = useState("");
  const [verifyStep, setVerifyStep] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/signup`, form);
      setVerifyStep(true);
      toast.success("Check your email for the 6-digit verification code.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not start registration");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event) => {
    event.preventDefault();
    if (busy || code.length !== 6) return;
    setBusy(true);
    try {
      const response = await axios.post(`${API}/auth/signup/verify`, { email: form.email, code });
      localStorage.setItem(TOKEN_KEY, response.data.token);
      notifyAthleteAuthChanged();
      toast.success(`Welcome to Fitygrity${response.data.name ? `, ${response.data.name.split(" ")[0]}` : ""}.`);
      navigate("/profile");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Invalid or expired code");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-28 sm:pt-36 pb-24" data-testid="signup-page">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl mx-auto">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <UserRoundPlus className="w-4 h-4" /> Athlete Registration
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#0D1117] tracking-tight mb-5">
            Create your race <span className="italic text-[#FF4E00]">passport.</span>
          </h1>
          <p className="text-base md:text-lg text-[#374151] max-w-2xl leading-relaxed mb-10">
            Register once with your race-day and emergency details. We’ll email you a one-time code to activate your account—no password needed.
          </p>

          {!verifyStep ? (
            <form onSubmit={submit} className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 sm:p-8 space-y-8" data-testid="signup-form">
              <section className="space-y-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Athlete Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Full name *"><input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="signup-name" /></Field>
                  <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="signup-email" /></Field>
                  <Field label="Phone *"><input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98100 12345" className={inputCls} data-testid="signup-phone" /></Field>
                  <Field label="Date of birth *"><input required type="date" value={form.dob} onChange={set("dob")} max={new Date().toISOString().slice(0, 10)} className={inputCls} data-testid="signup-dob" /></Field>
                  <Field label="Gender *">
                    <select required value={form.gender} onChange={set("gender")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="signup-gender">
                      <option value="">Select</option>
                      {GENDERS.map((gender) => <option key={gender} value={gender}>{gender}</option>)}
                    </select>
                  </Field>
                  <Field label="City *"><input required value={form.city} onChange={set("city")} placeholder="New Delhi" className={inputCls} data-testid="signup-city" /></Field>
                  <Field label="T-shirt size *">
                    <select required value={form.tshirt_size} onChange={set("tshirt_size")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="signup-tshirt">
                      <option value="">Select</option>
                      {SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>
                  </Field>
                  <Field label="Blood group *">
                    <select required value={form.blood_group} onChange={set("blood_group")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="signup-blood">
                      <option value="">Select</option>
                      {BLOOD_GROUPS.map((group) => <option key={group} value={group}>{group}</option>)}
                    </select>
                  </Field>
                </div>
              </section>

              <section className="space-y-5 border-t border-[#0D1117]/10 pt-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Emergency Contact</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <Field label="Name *"><input required value={form.emergency_name} onChange={set("emergency_name")} placeholder="Contact person" className={inputCls} data-testid="signup-emergency-name" /></Field>
                  <Field label="Phone *"><input required type="tel" value={form.emergency_phone} onChange={set("emergency_phone")} placeholder="+91 ..." className={inputCls} data-testid="signup-emergency-phone" /></Field>
                  <Field label="Relationship *"><input required value={form.emergency_relationship} onChange={set("emergency_relationship")} placeholder="e.g. Spouse" className={inputCls} data-testid="signup-emergency-relationship" /></Field>
                </div>
              </section>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 border-t border-[#0D1117]/10 pt-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#FF4E00]" /> Private — visible only to you and Race Control
                </p>
                <button type="submit" disabled={busy} data-testid="signup-submit" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 rounded-full transition-all duration-300">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Send Verification Code
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={verify} className="max-w-md mx-auto rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-8 sm:p-10 space-y-5" data-testid="signup-verify-form">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] flex items-center gap-3">
                <BadgeCheck className="w-4 h-4" /> Verify Email
              </p>
              <h2 className="font-serif text-3xl font-light text-[#0D1117] tracking-tight">Enter your 6-digit code.</h2>
              <p className="text-sm text-[#374151]">We sent it to <strong>{form.email}</strong>. It expires in 10 minutes.</p>
              <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono`} data-testid="signup-code" />
              <button type="submit" disabled={busy || code.length !== 6} data-testid="signup-verify" className="w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 rounded-full transition-all duration-300">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Activate Account
              </button>
              <button type="button" onClick={() => { setVerifyStep(false); setCode(""); }} className="w-full font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] hover:text-[#FF4E00] transition-colors" data-testid="signup-edit-details">
                Edit registration details
              </button>
            </form>
          )}

          <p className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] mt-8">
            Already registered? <Link to="/profile" data-testid="signup-login-link" className="text-[#FF4E00] hover:text-[#E04000]">Sign in with email code</Link>
          </p>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
